<script setup>
import BookFlipPage from '~/assets/icons/book-flip-page.svg'
import DangerAlert from '~/components/alerts/DangerAlert.vue'
import EpubRenderer from '~/components/ebook/EpubRenderer.vue'
import PDFPreview from '~/components/ebook/PDFPreview.vue'
import { useModal } from '~/composables/useModal.ts'

const props = defineProps({
  book: {
    type: Object,
    required: true
  },
  fileUrl: {
    type: String,
    default: ''
  },
  highlighted: {
    type: Boolean,
    default: false
  },
  staticModal: {
    type: Boolean,
    default: false
  }
})

const { show } = useModal('ebookPreview')
const isEpub = ref(props.fileUrl.includes('.epub'))

async function getFileType() {
  try {
    const response = await fetch(props.fileUrl, { method: 'HEAD' })

    return response.headers.get('Content-Type')
  } catch (error) {
    const { $sentryCaptureException } = useNuxtApp()
    $sentryCaptureException(
      `Error while checking file type for file: ${props.fileUrl}. ${error}`
    )

    return ''
  }
}

if (!isEpub.value) {
  const fileType = await getFileType()

  if (fileType === 'application/epub+zip') {
    isEpub.value = true
  }
}
</script>

<template>
  <div>
    <BaseButton
      id="gtm-track"
      :class="[
        'reset-btn-styling d-flex align-items-center text-start',
        { 'btn--highlighted': highlighted }
      ]"
      variant="link"
      size="sm"
      @click="show"
    >
      <BookFlipPage width="18.75" height="18.74" class="book-icon" />
      {{ $label('page.bookExample') }}
    </BaseButton>

    <BaseModal
      id="ebookPreview"
      hide-footer
      size="xl"
      body-class="overflow-hidden pb-md-32"
      :title="`${book.title} - ${book.author && book.author.name}`"
      :static-modal="staticModal"
    >
      <template #body>
        <EpubRenderer v-if="isEpub" :book="book" :file-url="fileUrl">
          <template #default="epubRendererProp">
            <DangerAlert v-if="epubRendererProp.errorWhileLoading">
              {{ $label('messages.errorOccuredMessage') }}
            </DangerAlert>
            <BaseSlider
              :next="() => epubRendererProp.changePage('next')"
              :prev="() => epubRendererProp.changePage('prev')"
            >
              <div
                id="viewer"
                :class="[
                  'viewer',
                  { 'viewer--error': epubRendererProp.errorWhileLoading },
                  { spinner: epubRendererProp.loadingBook },
                  epubRendererProp.transitionClass
                ]"
              >
                <BaseSpinner
                  v-show="
                    epubRendererProp.loadingBook &&
                    !epubRendererProp.errorWhileLoading
                  "
                  classes="spinner__icon text-soft"
                  type="grow"
                />
              </div>
            </BaseSlider>

            <div
              v-if="
                epubRendererProp.showPrev && !epubRendererProp.errorWhileLoading
              "
              class="arrow arrow--prev d-none d-md-block"
              @click="epubRendererProp.changePage('prev')"
            >
              <span variant="link" class="arrow__icon arrow__icon--prev"
                >‹</span
              >
            </div>
            <div
              v-if="
                epubRendererProp.showNext &&
                !epubRendererProp.errorWhileLoading &&
                !epubRendererProp.loadingBook
              "
              class="arrow arrow--next d-none d-md-block"
              @click="epubRendererProp.changePage('next')"
            >
              <span variant="link" class="arrow__icon arrow__icon--next"
                >›</span
              >
            </div>
          </template>
        </EpubRenderer>

        <PDFPreview v-else :file-url="fileUrl" />
      </template>
    </BaseModal>
  </div>
</template>

<style lang="scss" scoped>
.reset-btn-styling {
  color: $black;
  text-decoration: underline;
}

.btn--highlighted {
  color: $white;
  z-index: $popover-highlighted-el-zindex;
  position: relative;
}

.book-icon {
  margin-left: 2px;
  margin-right: 9px;
}

.modal__title {
  font-size: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alert-danger {
  margin-top: 1rem;
}

.arrow {
  bottom: 0;
  cursor: pointer;
  height: 100%;
  user-select: none;
  width: 50%;
}

.arrow--next {
  right: 0;
}

.arrow--prev {
  left: 0;
}

.arrow__icon {
  color: $mid-grey;
  font-family: arial, sans-serif;
  font-size: 64px;
  font-weight: $font-weight-semibold;
  position: absolute;
  text-decoration: none;
  top: 50%;
  transform: translateY(-50%);
}

.arrow__icon--next {
  right: 6px;
}

.arrow__icon--prev {
  left: 6px;
}

.viewer,
.spinner {
  display: flex;
  justify-content: center;
}

.spinner {
  align-items: center;
  height: 80vh;
  position: relative;
  width: 100%;
}

.spinner__icon {
  height: 6rem;
  position: absolute;
  width: 6rem;
}

.viewer--error {
  height: 65vh;
}

@include media-breakpoint-down(sm) {
  :deep(.modal-body) {
    padding: 0 8px;
  }
}

@include media-breakpoint-up(md) {
  .arrow__icon--next {
    right: 18px;
  }

  .arrow__icon--prev {
    left: 18px;
  }
}
</style>
